import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → People and roles — `/organization/people` (plan-014 B2; design §27.15.1; FR12-TEAM-01…05,
 * FR12-AUTH-08). Drawn to the prototype's `people` page (`design/WeTalk.dc.html`, the Organization block renderer):
 *
 *   Organization members      a table — person, organization role, workspaces, how they sign in, and the act the
 *                             signed-in person may take on that row ("Change role", "Grant role"); "Invite" in the head
 *   Invitations               pending, expired and revoked, apart from the members (FR12-TEAM-05), with Resend / Revoke
 *   What organization roles can do / What workspace roles can do
 *                             both matrices, every cell asked of the capability module (`@wetalk/domain`'s checked-in
 *                             matrix) — never a table typed here
 *   Transfer ownership        the Owner's form: the new Owner, the role kept afterwards, and the password again —
 *                             or, for an Owner with neither a password nor an authenticator, a confirmation with
 *                             Google or Microsoft first (ADR 0080 amendment, 2026-10-27), then Transfer again
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04) and offered only when the person's organization role allows it (the API
 * decides; the page only refrains from offering what would be refused). "Grant role" for someone with workspace roles
 * only opens the invitation form for their address: an organization role is accepted, never imposed.
 *
 * NO EMAIL SENDER YET. While the API answers an invitation with `delivery: "share_link"`, the link is shown once, in
 * a dialog, for the inviter to pass on — the API's own words, never a pretended email.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import {
  ACCOUNT_ROLE,
  MEMBER_ROLE,
  accountInviteRefusal,
  accountRemoveRefusal,
  accountRoleChangeRefusal,
  holdsAccountCapability,
  holdsWorkspaceCapability,
  needsStepUp,
  type AccountCapability,
  type AccountRole,
  type MemberRole,
  type WorkspaceCapability,
} from "@wetalk/domain";
import { Button, Dialog, Field, Input, Select } from "@wetalk/ui";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../../../api/query.js";
import type {
  AccountInvitationView,
  AccountMemberView,
  InvitableAccountRole,
  SentAccountInvitationView,
} from "../../../api/slice/account-team.js";
import type { WorkspaceRoleView } from "../../../api/gateway.js";
import type { IdentityConfirmationFactor } from "../../../api/slice/account-security.js";
import { GLYPH } from "../../../shell/glyph.js";
import { accountProfileKey, roleHolds, useOpenAccount } from "../overview/account-overview-screen.js";
import { Block, BlockNote, FormPanel, ItemColumn, ItemRow, OrganizationPage, type Outcome } from "../page/organization-page.js";
import { ACCOUNT_ROLE_WORD, WORKSPACE_ROLE_WORD, expiryText, shortDateText, signInText } from "../page/role-copy.js";
import { WorkspaceChoiceStatus, openWorkspaceOf, organizationWorkspaceReadOf, type OrganizationWorkspaceRead } from "../page/workspace-choice.js";
import { PERSONAL_SECURITY_KEY } from "../../personal-security/personal-security-screen.js";
import {
  ConfirmIdentityDialog,
  isStepUpRequired,
  stepUpReturnOf,
  stepUpReturnOutcome,
  type PendingConfirmation,
} from "../security/confirm-identity.js";

/** This page's path: where a Google or Microsoft confirmation comes back to. */
const PEOPLE_PATH = "/organization/people";

/** The page's search parameters: `step_up` only on the way back from a Google or Microsoft confirmation. */
export interface AccountTeamSearch {
  readonly step_up?: "confirmed" | "cancelled" | "failed";
}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page.
 */
export function accountTeamSearchOf(raw: Readonly<Record<string, unknown>>): AccountTeamSearch {
  return stepUpReturnOf(raw);
}

export interface AccountTeamScreenProps {
  readonly search: AccountTeamSearch;
  readonly onSearch: (next: AccountTeamSearch) => void;
}

const memberKey = (accountId: string) => ["account-team", "members", accountId] as const;
const invitationKey = (accountId: string) => ["account-team", "invitations", accountId] as const;

const INVITABLE_ROLE: readonly InvitableAccountRole[] = ["admin", "finance", "viewer"];

// ── the two matrices, asked of the capability module ──────────────────────────────────────────────────────────

interface AccountMatrixRow {
  readonly label: string;
  readonly capability: readonly AccountCapability[];
  /** The Admin limit (design §27.5.2: "non-owner targets"): an Admin acts on everyone but Owners. */
  readonly ownerLimited?: true;
}

const ACCOUNT_MATRIX_ROW: readonly AccountMatrixRow[] = [
  { get label() { return portalText("core", "account_admin.see_the_profile_and_workspace_list_ea2b65"); }, capability: ["account.profile.read", "account.workspace_metadata.read"] },
  { get label() { return portalText("core", "account_admin.create_and_manage_workspaces_2b45e4"); }, capability: ["account.workspace.create", "account.workspace.manage"] },
  { get label() { return portalText("core", "account_admin.invite_people_and_change_roles_ce2d62"); }, capability: ["account_membership.invite", "account_membership.role_change"], ownerLimited: true },
  { get label() { return portalText("core", "account_admin.see_balances_invoices_and_statements_af6071"); }, capability: ["account.finance.read"] },
  { get label() { return portalText("core", "account_admin.change_plan_cards_and_funding_8b0012"); }, capability: ["account.finance.manage"] },
  { get label() { return portalText("core", "account_admin.set_sign_in_policy_e71d08"); }, capability: ["account.sign_in_policy.write"] },
  { get label() { return portalText("core", "account_admin.transfer_ownership_or_close_the_organization_e93f8f"); }, capability: ["account.ownership.transfer", "account.close"] },
];

/** One cell of "What organization roles can do". */
export function accountMatrixCell(role: AccountRole, row: AccountMatrixRow): string {
  if (!row.capability.every((one) => holdsAccountCapability(role, one))) return "—";
  if (row.capability.some((one) => needsStepUp(one))) return portalText("core", "account_admin.with_password_again_626156");
  if (row.ownerLimited === true && accountRoleChangeRefusal({ caller: role, targetRole: "owner", toRole: "viewer", self: false }) !== null) {
    return portalText("core", "account_admin.except_owners_eda44a");
  }
  return portalText("core", "account_admin.yes_5397e0");
}

/** The workspace columns: Owner and Admin share one (they differ only in lifecycle, which no row covers). */
const WORKSPACE_COLUMN: readonly { readonly label: string; readonly role: MemberRole }[] = [
  { get label() { return portalText("core", "account_admin.owner_admin_958e03"); }, role: "owner" },
  { get label() { return portalText("core", "account_admin.member_6853c9"); }, role: "member" },
  { get label() { return portalText("core", "account_admin.viewer_dbad9f"); }, role: "viewer" },
  { get label() { return portalText("core", "account_admin.developer_7e08eb"); }, role: "developer" },
  { get label() { return portalText("core", "account_admin.campaign_manager_ce843f"); }, role: "campaign_manager" },
  { get label() { return portalText("core", "account_admin.billing_abaec4"); }, role: "billing" },
];

const holds = (role: MemberRole, ...capability: WorkspaceCapability[]): boolean => capability.every((one) => holdsWorkspaceCapability(role, one));

/** "What workspace roles can do": each row's words for a role, from what the role holds. */
const WORKSPACE_MATRIX_ROW: readonly { readonly label: string; readonly cell: (role: MemberRole) => string }[] = [
  { get label() { return portalText("core", "account_admin.summaries_and_analytics_731c3b"); }, get cell() { return (role: MemberRole) => (holds(role, "analytics.read") ? portalText("core", "account_admin.yes_5397e0") : holds(role, "billing.read") ? portalText("core", "account_admin.spend_only_c9c40b") : "—"); } },
  { get label() { return portalText("core", "account_admin.read_conversations_fdbc22"); }, get cell() { return (role: MemberRole) => (holds(role, "conversation.read") ? (holds(role, "caller_detail.read") ? portalText("core", "account_admin.yes_5397e0") : portalText("core", "account_admin.masked_3c7ede")) : "—"); } },
  { get label() { return portalText("core", "account_admin.play_recordings_see_caller_details_81284e"); }, get cell() { return (role: MemberRole) => (holds(role, "recording.play", "caller_detail.read") ? portalText("core", "account_admin.yes_5397e0") : "—"); } },
  { get label() { return portalText("core", "account_admin.edit_agents_and_documents_80d8ad"); }, get cell() { return (role: MemberRole) => (holds(role, "voice_agent.write", "agent_document.write") ? portalText("core", "account_admin.yes_5397e0") : "—"); } },
  {
    get label() { return portalText("core", "account_admin.connections_and_api_keys_c1b9a1"); },
    get cell() { return (role: MemberRole) =>
      holds(role, "api_key.manage") ? portalText("core", "account_admin.yes_5397e0") : holds(role, "api_key.integration_manage") ? portalText("core", "account_admin.scoped_keys_73302c") : holds(role, "channel_binding.write") ? portalText("core", "account_admin.some_00b2aa") : "—"; },
  },
  { get label() { return portalText("core", "account_admin.run_campaigns_use_contact_lists_782515"); }, get cell() { return (role: MemberRole) => (holds(role, "campaign.operate", "contact_list.manage") ? portalText("core", "account_admin.yes_5397e0") : "—"); } },
  { get label() { return portalText("core", "account_admin.manage_the_workspace_team_bf87fa"); }, get cell() { return (role: MemberRole) => (holds(role, "membership.invite", "membership.role_change") ? portalText("core", "account_admin.yes_5397e0") : "—"); } },
  { get label() { return portalText("core", "account_admin.see_workspace_spend_029762"); }, get cell() { return (role: MemberRole) => (holds(role, "billing.read") ? portalText("core", "account_admin.yes_5397e0") : "—"); } },
];

// ── the members table ─────────────────────────────────────────────────────────────────────────────────────────

function workspaceText(member: AccountMemberView): string {
  if (member.workspaceRole.length === 0) return member.accountRole === "finance" ? portalText("core", "account_admin.none_finance_only_567acc") : portalText("core", "account_admin.none_6eef66");
  if (member.workspaceRole.length > 2) {
    const roles = new Set(member.workspaceRole.map((one) => one.memberRole));
    const only = [...roles][0];
    if (roles.size === 1 && only !== undefined) return portalText("core", "account_admin._in_053ffc", { value0: WORKSPACE_ROLE_WORD[only], value1: String(member.workspaceRole.length) });
  }
  return member.workspaceRole.map((one) => portalText("core", "account_admin._in_053ffc", { value0: WORKSPACE_ROLE_WORD[one.memberRole], value1: one.workspaceName ?? portalText("core", "account_admin.a_workspace_b7c8a4") })).join(", ");
}

/** The roles the caller may move `member` to (never Owner: ownership is transferred). */
function assignableFor(caller: AccountRole | null, member: AccountMemberView, self: boolean): readonly InvitableAccountRole[] {
  const current = member.accountRole;
  if (current === null) return [];
  return INVITABLE_ROLE.filter((one) => one !== current && accountRoleChangeRefusal({ caller, targetRole: current, toRole: one, self }) === null);
}

function MemberTable(props: {
  readonly member: readonly AccountMemberView[];
  readonly caller: AccountRole | null;
  readonly selfPersonId: string | null;
  readonly onChange: (member: AccountMemberView) => void;
  readonly onGrant: (member: AccountMemberView) => void;
}): ReactNode {
  usePortalTranslation("core");
  const canInvite = accountInviteRefusal(props.caller, "viewer") === null;
  return (
    <div className="op-table-wrap">
      <table className="table op-table">
        <thead>
          <tr>
            <th>{portalText("core", "account_admin.person_8c41ae")}</th>
            <th>{portalText("core", "account_admin.organization_role_cb7d96")}</th>
            <th>{portalText("core", "account_admin.workspaces_205b45")}</th>
            <th>{portalText("core", "account_admin.sign_in_4cd5be")}</th>
            <th>
              <span className="visually-hidden">{portalText("core", "account_admin.act_138a10")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.member.map((one) => {

            const self = one.personId === props.selfPersonId;
            const changeable =
              one.accountRole !== null &&
              (assignableFor(props.caller, one, self).length > 0 || accountRemoveRefusal(props.caller, one.accountRole) === null);
            const weak = !one.signIn.authenticator && one.signIn.external.length > 0;
            return (
              <tr key={one.personId}>
                <td>
                  <span className="op-cell-person">{one.displayName ?? portalText("core", "account_admin.unknown_person_afff50")}</span>
                  <span className="op-cell-sub">{one.email ?? "—"}</span>
                </td>
                <td>
                  {one.accountRole === null ? (
                    <span className="op-cell-dim">{portalText("core", "account_admin.no_organization_role_df6936")}</span>
                  ) : (
                    <span className={one.accountRole === "owner" ? "tag tag-accent" : "tag tag-neutral"}>{ACCOUNT_ROLE_WORD[one.accountRole]}</span>
                  )}
                </td>
                <td>
                  <span className={one.workspaceRole.length === 0 ? "op-cell-dim" : "op-cell"}>{workspaceText(one)}</span>
                </td>
                <td>
                  <span className={weak ? "op-cell-negative" : "op-cell-dim"}>{signInText(one.signIn)}</span>
                </td>
                <td className="op-cell-action">
                  {changeable ? (
                    <button type="button" className="op-link" onClick={() => props.onChange(one)}>{portalText("core", "account_admin.change_role_2b9bfd")}</button>
                  ) : one.accountRole === null && canInvite && one.email !== null ? (
                    <button type="button" className="op-link" onClick={() => props.onGrant(one)}>{portalText("core", "account_admin.grant_role_d9dd74")}</button>
                  ) : (
                    <span className="op-cell-dim">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ChangeRoleDialog(props: {
  readonly accountId: string;
  readonly member: AccountMemberView | null;
  readonly caller: AccountRole | null;
  readonly self: boolean;
  readonly onDone: (outcome: Outcome | null) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const member = props.member;
  const choices = member === null ? [] : assignableFor(props.caller, member, props.self);
  const [picked, setPicked] = useState<InvitableAccountRole | "">("");
  const name = member?.displayName ?? portalText("core", "account_admin.this_person_685984");
  const finish = async (outcome: Outcome): Promise<void> => {
    setPicked("");
    await queryClient.invalidateQueries({ queryKey: memberKey(props.accountId) });
    props.onDone(outcome);
  };
  const change = useMutation({
    mutationFn: async (role: InvitableAccountRole) => {
      if (member?.accountMembershipId === null || member === null || member.revision === null) throw new Error(portalText("core", "account_admin.not_a_member_e71c57"));
      await gateway.changeAccountMemberRole(props.accountId, member.accountMembershipId, role, member.revision);
      return role;
    },
    onSuccess: (role) => finish({ tone: "done", get text() { return portalText("core", "account_admin._is_now_3073b5", { value0: name, value1: ACCOUNT_ROLE_WORD[role] }); } }),
    onError: (raised: Error) => finish({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.that_role_could_not_be_changed_ffddc0")); } }),
  });
  const remove = useMutation({
    mutationFn: async () => {
      if (member?.accountMembershipId === null || member === null || member.revision === null) throw new Error(portalText("core", "account_admin.not_a_member_e71c57"));
      await gateway.removeAccountMember(props.accountId, member.accountMembershipId, member.revision);
    },
    onSuccess: () => finish({ tone: "done", get text() { return portalText("core", "account_admin._no_longer_has_an_organization_role_their_workspace_roles_are_unc_b42ea2", { value0: name }); } }),
    onError: (raised: Error) => finish({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.that_role_could_not_be_removed_7a6d39")); } }),
  });
  const canRemove = member?.accountRole !== null && member !== null && member.accountRole !== null && accountRemoveRefusal(props.caller, member.accountRole) === null;
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (picked !== "") change.mutate(picked);
  };
  return (
    <Dialog open={member !== null} title={portalText("core", "account_admin.change_organization_role_35f593")} onClose={() => props.onDone(null)}>
      <form className="op-dialog-form" onSubmit={submit}>
        <p className="op-dialog-text">
          {name}{" "}{portalText("core", "account_admin.is_b47f36")}{" "}{member?.accountRole === null || member === null ? portalText("core", "account_admin.not_in_an_organization_role_94cf75") : ACCOUNT_ROLE_WORD[member.accountRole]}{portalText("core", "account_admin._their_open_pages_reload_the_next_time_they_act_ownership_moves_o_3bf0f2")}</p>
        {choices.length === 0 ? null : (
          <Field label={portalText("core", "account_admin.new_role_165fb5")}>
            {(control) => (
              <Select {...control} value={picked} onChange={(event) => setPicked(event.target.value as InvitableAccountRole | "")}>
                <option value="">{portalText("core", "account_admin.choose_a_role_215ccf")}</option>
                {choices.map((one) => (
                  <option key={one} value={one}>
                    {ACCOUNT_ROLE_WORD[one]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <div className="op-dialog-actions">
          {canRemove ? (
            <Button variant="secondary" onClick={() => remove.mutate()} disabled={remove.isPending || change.isPending}>{portalText("core", "account_admin.remove_from_organization_f684c5")}</Button>
          ) : null}
          {choices.length === 0 ? null : (
            <Button variant="primary" type="submit" disabled={picked === "" || change.isPending || remove.isPending}>{portalText("core", "account_admin.change_role_2b9bfd")}</Button>
          )}
        </div>
      </form>
    </Dialog>
  );
}

// ── invitations ───────────────────────────────────────────────────────────────────────────────────────────────

interface WorkspaceRoleDraft {
  readonly workspaceId: string;
  readonly memberRole: WorkspaceRoleView;
}

function InviteDialog(props: {
  readonly accountId: string;
  readonly open: boolean;
  readonly initialEmail: string;
  readonly caller: AccountRole | null;
  /** The organization's open workspaces, as its profile read stands (loading, failed or ready). */
  readonly workspace: OrganizationWorkspaceRead;
  readonly onSent: (sent: SentAccountInvitationView) => void;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [email, setEmail] = useState(props.initialEmail);
  const [accountRole, setAccountRole] = useState<InvitableAccountRole | "">(props.initialEmail === "" ? "" : "viewer");
  const [rows, setRows] = useState<readonly WorkspaceRoleDraft[]>([]);
  const [problem, setProblem] = usePortalMessage();
  const openWorkspace = openWorkspaceOf(props.workspace);
  const workspaceRoleChoice = MEMBER_ROLE.filter((one) => one !== "owner" || props.caller === "owner");
  const roleChoice = INVITABLE_ROLE.filter((one) => accountInviteRefusal(props.caller, one) === null);
  const send = useMutation({
    mutationFn: () =>
      gateway.sendAccountInvitation(props.accountId, {
        email: email.trim(),
        accountRole: accountRole === "" ? null : accountRole,
        workspaceRole: rows,
      }),
    onSuccess: (sent) => {
      setEmail("");
      setAccountRole("");
      setRows([]);
      setProblem(null);
      props.onSent(sent);
    },
    onError: (raised: Error) => setProblem(() => (apiProblemText(raised, portalText("core", "account_admin.the_invitation_could_not_be_sent_6d005c")))),
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (email.trim() === "") {
      setProblem(() => (portalText("core", "account_admin.enter_the_address_to_invite_31b550")));
      return;
    }
    if (accountRole === "" && rows.length === 0) {
      setProblem(() => (portalText("core", "account_admin.choose_an_organization_role_a_workspace_role_or_both_604e4a")));
      return;
    }
    send.mutate();
  };
  const unused = openWorkspace.filter((one) => !rows.some((row) => row.workspaceId === one.workspaceId));
  return (
    <Dialog open={props.open} title={portalText("core", "account_admin.invite_someone_9768ea")} onClose={props.onClose}>
      <form className="op-dialog-form" onSubmit={submit}>
        <p className="op-dialog-text">{portalText("core", "account_admin.an_invitation_grants_exactly_the_roles_it_names_workspaces_added_6f5115")}</p>
        <Field label={portalText("core", "auth.email_84add5")} {...(problem === null ? {} : { error: problem })}>
          {(control) => <Input {...control} type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />}
        </Field>
        <Field label={portalText("core", "account_admin.organization_role_cb7d96")} description={portalText("core", "account_admin.owner_can_only_be_given_by_a_transfer_269610")}>
          {(control) => (
            <Select {...control} value={accountRole} onChange={(event) => setAccountRole(event.target.value as InvitableAccountRole | "")}>
              <option value="">{portalText("core", "account_admin.none_workspace_access_only_055f0f")}</option>
              {roleChoice.map((one) => (
                <option key={one} value={one}>
                  {ACCOUNT_ROLE_WORD[one]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <WorkspaceChoiceStatus read={props.workspace} />
        {/* The first workspace is chosen right here, from the organization's open workspaces (owner, 2026-11-03:
            "i cannot select from dropdown"); "No workspace role" leaves the invitation to the organization role. */}
        {rows.length === 0 && openWorkspace.length > 0 ? (
          <Field label={portalText("core", "account_admin.workspace_4ca0a7")}>
            {(control) => (
              <Select
                {...control}
                value=""
                onChange={(event) => {
                  if (event.target.value !== "") setRows([{ workspaceId: event.target.value, memberRole: "viewer" }]);
                }}
              >
                <option value="">{portalText("core", "account_admin.workspace_choice_no_role")}</option>
                {openWorkspace.map((one) => (
                  <option key={one.workspaceId} value={one.workspaceId}>
                    {one.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        {rows.map((row, index) => (
          <div key={row.workspaceId} className="op-workspace-pick">
            <Field label={index === 0 ? portalText("core", "account_admin.workspace_4ca0a7") : portalText("core", "account_admin.workspace_f1c1f3", { value0: String(index + 1) })}>
              {(control) => (
                <Select
                  {...control}
                  value={row.workspaceId}
                  onChange={(event) => {
                    const picked = event.target.value;
                    // "No workspace role" takes this workspace off the invitation.
                    setRows(picked === "" ? rows.filter((_, at) => at !== index) : rows.map((one, at) => (at === index ? { ...one, workspaceId: picked } : one)));
                  }}
                >
                  <option value="">{portalText("core", "account_admin.workspace_choice_no_role")}</option>
                  {openWorkspace
                    .filter((one) => one.workspaceId === row.workspaceId || !rows.some((other) => other.workspaceId === one.workspaceId))
                    .map((one) => (
                      <option key={one.workspaceId} value={one.workspaceId}>
                        {one.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            <Field label={portalText("core", "account_admin.workspace_role_ba3352")}>
              {(control) => (
                <Select
                  {...control}
                  value={row.memberRole}
                  onChange={(event) => setRows(rows.map((one, at) => (at === index ? { ...one, memberRole: event.target.value as WorkspaceRoleView } : one)))}
                >
                  {workspaceRoleChoice.map((one) => (
                    <option key={one} value={one}>
                      {WORKSPACE_ROLE_WORD[one]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        ))}
        <div className="op-dialog-actions">
          {rows.length === 0 || unused[0] === undefined ? null : (
            <Button variant="secondary" onClick={() => setRows([...rows, { workspaceId: unused[0]?.workspaceId ?? "", memberRole: "viewer" }])}>{portalText("core", "account_admin.add_a_workspace_role_dbc5e3")}</Button>
          )}
          <Button variant="primary" type="submit" disabled={send.isPending}>{portalText("core", "account_admin.send_invitation_abb6cb")}</Button>
        </div>
      </form>
    </Dialog>
  );
}

function ShareLinkDialog(props: { readonly sent: SentAccountInvitationView | null; readonly onClose: () => void }): ReactNode {
  usePortalTranslation("core");
  const path = props.sent?.invitationPath ?? null;
  const link = path === null ? "" : `${window.location.origin}${path}`;
  return (
    <Dialog open={props.sent !== null && path !== null} title={portalText("core", "account_admin.send_this_link_yourself_b83d98")} onClose={props.onClose} dismissible={false}>
      <div className="op-dialog-form">
        <p className="op-dialog-text">{portalText("core", "account_admin.email_is_not_connected_yet_so_wetalk_did_not_send_this_invitation_b8a444")}{" "}{props.sent?.invitation.email ?? portalText("core", "account_admin.them")}{" "}{portalText("core", "account_admin.yourself_it_is_shown_only_now_works_once_and_expires_in_7_days_31202c")}</p>
        <code className="op-share">{link}</code>
        <div className="op-dialog-actions">
          <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(link)}>{portalText("core", "account_admin.copy_link_2f84ee")}</Button>
          <Button variant="primary" onClick={props.onClose}>{portalText("core", "first_run.done_e9b450")}</Button>
        </div>
      </div>
    </Dialog>
  );
}

function invitationRolesText(invitation: AccountInvitationView): string {
  const roles = invitation.workspaceRole.map((one) => portalText("core", "account_admin._in_053ffc", { value0: WORKSPACE_ROLE_WORD[one.memberRole], value1: one.workspaceName ?? portalText("core", "account_admin.a_workspace_b7c8a4") }));
  const account = invitation.accountRole === null ? [] : [`${ACCOUNT_ROLE_WORD[invitation.accountRole]}${roles.length === 0 ? portalText("core", "account_admin._organization_only_3bcd9d") : ""}`];
  return [...account, ...roles].join(" · ");
}

const STATE_WORD: Readonly<Record<"pending" | "expired" | "revoked", string>> = { get pending() { return portalText("core", "account_admin.pending_96f608"); }, get expired() { return portalText("core", "account_admin.expired_a689a9"); }, get revoked() { return portalText("core", "account_admin.revoked_85f17a"); } };

function InvitationColumn(props: {
  readonly accountId: string;
  readonly invitation: readonly AccountInvitationView[];
  readonly canInvite: boolean;
  readonly now: Date;
  readonly onOutcome: (outcome: Outcome) => void;
  readonly onShare: (sent: SentAccountInvitationView) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: invitationKey(props.accountId) });
  const resend = useMutation({
    mutationFn: (invitation: AccountInvitationView) => gateway.resendAccountInvitation(props.accountId, invitation.accountInvitationId),
    onSuccess: async (sent, invitation) => {
      await refresh();
      if (sent.delivery === "share_link") props.onShare(sent);
      props.onOutcome({
        tone: "done",
        get text() { return invitation.state === "expired" ? portalText("core", "account_admin.a_new_invitation_was_sent_with_a_fresh_link_39336d") : portalText("core", "account_admin.resent_the_earlier_link_no_longer_works_4b74fe"); },
      });
    },
    onError: async (raised: Error) => {
      await refresh();
      props.onOutcome({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.the_invitation_could_not_be_resent_b42ad6")); } });
    },
  });
  const revoke = useMutation({
    mutationFn: (invitation: AccountInvitationView) => gateway.revokeAccountInvitation(props.accountId, invitation.accountInvitationId),
    onSuccess: async () => {
      await refresh();
      props.onOutcome({ tone: "done", get text() { return portalText("core", "account_admin.invitation_revoked_460e21"); } });
    },
    onError: async (raised: Error) => {
      await refresh();
      props.onOutcome({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.the_invitation_could_not_be_revoked_c04873")); } });
    },
  });
  const shown = props.invitation.filter(
    (one): one is AccountInvitationView & { readonly state: "pending" | "expired" | "revoked" } =>
      one.state === "pending" || one.state === "expired" || one.state === "revoked",
  );
  if (shown.length === 0) return <BlockNote>{portalText("core", "account_admin.no_invitations_are_pending_expired_or_revoked_c4e14f")}</BlockNote>;
  return (
    <ItemColumn label={portalText("core", "account_admin.invitations_9b69a3")}>
      {shown.map((one) => {

        const by = one.state === "revoked" ? (one.revokedByName === null ? "" : portalText("core", "account_admin._revoked_by_cdfa50", { value0: one.revokedByName })) : one.invitedByName === null ? "" : portalText("core", "account_admin._sent_by_fef27b", { value0: one.invitedByName });
        const meta = one.state === "revoked" ? shortDateText(one.revokedAt ?? one.createdAt) : expiryText(one.expiresAt, props.now);
        return (
          <ItemRow
            key={one.accountInvitationId}
            glyph={one.state === "revoked" ? GLYPH.envelopeSimpleOpen : GLYPH.envelopeSimple}
            tone={one.state === "pending" ? "accent" : "neutral"}
            title={one.email}
            tag={STATE_WORD[one.state]}
            dek={`${invitationRolesText(one)}${by}`}
            meta={meta}
            actions={
              props.canInvite && one.state !== "revoked" ? (
                <>
                  <Button variant="secondary" onClick={() => resend.mutate(one)} disabled={resend.isPending}>{portalText("core", "account_admin.resend_0f4f46")}</Button>
                  {one.state === "pending" ? (
                    <Button variant="secondary" onClick={() => revoke.mutate(one)} disabled={revoke.isPending}>{portalText("core", "account_admin.revoke_0be720")}</Button>
                  ) : null}
                </>
              ) : undefined
            }
          />
        );
      })}
    </ItemColumn>
  );
}

// ── the two matrices ──────────────────────────────────────────────────────────────────────────────────────────

function AccountMatrix(): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-table-wrap">
      <table className="table op-table">
        <thead>
          <tr>
            <th>
              <span className="visually-hidden">{portalText("core", "account_admin.act_138a10")}</span>
            </th>
            {ACCOUNT_ROLE.map((role) => (
              <th key={role} className="op-center">
                {ACCOUNT_ROLE_WORD[role]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ACCOUNT_MATRIX_ROW.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              {ACCOUNT_ROLE.map((role) => (
                <td key={role} className="op-center">
                  {accountMatrixCell(role, row)}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td>{portalText("core", "account_admin.read_conversations_fdbc22")}</td>
            {ACCOUNT_ROLE.map((role) => (
              <td key={role} className="op-center">{portalText("core", "account_admin.needs_a_workspace_role_41c5ce")}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function WorkspaceMatrix(): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="op-table-wrap">
      <table className="table op-table">
        <thead>
          <tr>
            <th>
              <span className="visually-hidden">{portalText("core", "account_admin.act_138a10")}</span>
            </th>
            {WORKSPACE_COLUMN.map((one) => (
              <th key={one.label}>{one.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {WORKSPACE_MATRIX_ROW.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              {WORKSPACE_COLUMN.map((one) => (
                <td key={one.label}>{row.cell(one.role)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ownership transfer ────────────────────────────────────────────────────────────────────────────────────────

function TransferForm(props: {
  readonly accountId: string;
  readonly member: readonly AccountMemberView[];
  readonly selfPersonId: string | null;
  readonly onOutcome: (outcome: Outcome) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const candidate = props.member.filter((one) => one.accountRole !== null && one.accountRole !== "owner" && one.emailVerified && one.personId !== props.selfPersonId);
  const [to, setTo] = useState("");
  const [after, setAfter] = useState<InvitableAccountRole>("admin");
  // plan-014 C2: the confirmation is the person's choice — the password, the authenticator's code or a recovery code —
  // so an Owner who signs in only with Google or Microsoft can confirm with their authenticator.
  const self = props.member.find((one) => one.personId === props.selfPersonId);
  const [factor, setFactor] = useState<IdentityConfirmationFactor>(self === undefined || self.signIn.password ? "password" : "totp");
  const [password, setPassword] = useState("");
  // Neither a password nor an authenticator: the transfer is sent as it is, and a `step_up_required` answer opens the
  // confirmation with Google or Microsoft, which comes back to this page (ADR 0080 amendment, 2026-10-27).
  const methods = useQuery({ queryKey: PERSONAL_SECURITY_KEY.methods, queryFn: () => gateway.readMySignInMethods() });
  const withProvider =
    methods.data !== undefined &&
    !methods.data.password.enabled &&
    methods.data.totp.state !== "active" &&
    (methods.data.google.linked || methods.data.microsoft.linked);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [problem, setProblem] = usePortalMessage();
  // One key per transfer: a retry of the same transfer (same new Owner, same role afterwards) is recognised; another
  // choice is another request with its own key (plan-014 review I-16 — a failed transfer to X used to make a transfer
  // to Y fail `idempotency_key_reused_with_different_body` until reload).
  const [idempotencyKeys] = useState(() => new Map<string, string>());
  const keyOf = (target: string): string => {
    const held = idempotencyKeys.get(target);
    if (held !== undefined) return held;
    const minted = crypto.randomUUID();
    idempotencyKeys.set(target, minted);
    return minted;
  };
  const transfer = useMutation({
    mutationFn: async () => {
      if (!withProvider) await gateway.confirmIdentity(factor, factor === "password" ? password : password.replace(/\s/g, ""));
      await gateway.transferAccountOwnership(props.accountId, to, after, keyOf(`${to}|${after}`));
    },
    onSuccess: async () => {
      const name = candidate.find((one) => one.personId === to)?.displayName ?? portalText("core", "account_admin.the_new_owner_5f725f");
      setPassword("");
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: ["account-team"] });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
      props.onOutcome({ tone: "done", get text() { return portalText("core", "account_admin.ownership_moved_to_you_are_now_9bf307", { value0: name, value1: ACCOUNT_ROLE_WORD[after] }); } });
    },
    onError: (raised: Error) => {
      setPassword("");
      if (withProvider && isStepUpRequired(raised)) {
        setPending({ retry: async () => {} });
        return;
      }
      if (raised instanceof ApiError && raised.code === "credential_invalid") {
        setProblem(() => (factor === "password" ? portalText("core", "start.that_password_is_not_right_930b19") : portalText("core", "start.that_code_is_not_right_273772")));
        return;
      }
      if (raised instanceof ApiError && raised.code === "validation_failed" && raised.field === "factor") {
        setProblem(() => (portalText("core", "account_admin.you_have_no_authenticator_app_set_up_confirm_with_your_password_i_1e828a")));
        return;
      }
      setProblem(() => (apiProblemText(raised, portalText("core", "account_admin.ownership_could_not_be_transferred_3866be"))));
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (to === "" || (!withProvider && password === "")) {
      setProblem(() => (to === "" ? portalText("core", "account_admin.choose_the_new_owner_abeafc") : factor === "password" ? portalText("core", "account_admin.enter_your_password_46affb") : portalText("core", "account_admin.enter_the_code_30b9ae")));
      return;
    }
    transfer.mutate();
  };
  if (candidate.length === 0) {
    return <BlockNote>{portalText("core", "account_admin.nobody_can_take_ownership_yet_the_new_owner_must_be_a_member_of_t_317081")}</BlockNote>;
  }
  return (
    <>
    <FormPanel onSubmit={submit} label={portalText("core", "account_admin.transfer_ownership_a6819e")}>
      <Field label={portalText("core", "account_admin.new_owner_d43bde")}>
        {(control) => (
          <Select {...control} value={to} onChange={(event) => setTo(event.target.value)}>
            <option value="">{portalText("core", "account_admin.choose_someone_a0b6e9")}</option>
            {candidate.map((one) => (
              <option key={one.personId} value={one.personId}>
                {one.displayName ?? one.email ?? one.personId}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={portalText("core", "account_admin.your_role_afterwards_ba131f")}>
        {(control) => (
          <Select {...control} value={after} onChange={(event) => setAfter(event.target.value as InvitableAccountRole)}>
            {INVITABLE_ROLE.map((one) => (
              <option key={one} value={one}>
                {ACCOUNT_ROLE_WORD[one]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {withProvider ? (problem === null ? null : <p className="c-note c-note-critical" role="alert">{problem}</p>) : (
      <>
      <Field label={portalText("core", "account_admin.confirm_with_7996ed")}>
        {(control) => (
          <Select
            {...control}
            value={factor}
            onChange={(event) => {
              setFactor(event.target.value as IdentityConfirmationFactor);
              setPassword("");
            }}
          >
            <option value="password">{portalText("core", "account_admin.my_password_d5cd66")}</option>
            <option value="totp">{portalText("core", "account_admin.my_authenticator_app_8ba7df")}</option>
            <option value="recovery_code">{portalText("core", "account_admin.a_recovery_code_b09d41")}</option>
          </Select>
        )}
      </Field>
      <Field
        label={factor === "password" ? portalText("core", "start.your_password_26d745") : factor === "totp" ? portalText("core", "start.authenticator_code_2908b4") : portalText("core", "auth.recovery_code_3cb5fd")}
        description={portalText("core", "account_admin.required_for_this_change_dd3920")}
        {...(problem === null ? {} : { error: problem })}
      >
        {(control) => (
          <Input
            {...control}
            type={factor === "password" ? "password" : "text"}
            value={password}
            placeholder={factor === "password" ? "••••••••" : ""}
            autoComplete={factor === "password" ? "current-password" : "one-time-code"}
            onChange={(event) => setPassword(event.target.value)}
          />
        )}
      </Field>
      </>
      )}
      <span className="op-form-actions">
        <Button variant="primary" type="submit" disabled={transfer.isPending}>{portalText("core", "account_admin.transfer_cbb4cc")}</Button>
      </span>
    </FormPanel>
    <ConfirmIdentityDialog
      pending={pending}
      preferred="totp"
      returnPath={PEOPLE_PATH}
      onDone={() => setPending(null)}
      onProblem={(text) => setProblem(text)}
    />
    </>
  );
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────────

export function AccountTeamScreen(props: AccountTeamScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account_membership.read");
  const canInvite = roleHolds(open.accountRole, "account_membership.invite");
  const canTransfer = roleHolds(open.accountRole, "account.ownership.transfer");
  // A Google or Microsoft confirmation's return (`?step_up=`) is shown once, then dropped from the address.
  const [outcome, setOutcome] = useState<Outcome | null>(() => stepUpReturnOutcome(props.search.step_up));
  const { onSearch } = props;
  const returned = props.search.step_up;
  useEffect(() => {
    if (returned !== undefined) onSearch({});
  }, [returned, onSearch]);
  const [changing, setChanging] = useState<AccountMemberView | null>(null);
  const [inviting, setInviting] = useState<{ readonly email: string } | null>(null);
  const [sharing, setSharing] = useState<SentAccountInvitationView | null>(null);
  const profile = useQuery({
    queryKey: accountProfileKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountProfile(accountId ?? ""),
    enabled: accountId !== null,
  });
  const member = useQuery({ queryKey: memberKey(accountId ?? "none"), queryFn: () => gateway.readAccountMembers(accountId ?? ""), enabled: accountId !== null && canRead });
  const invitation = useQuery({
    queryKey: invitationKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountInvitations(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const queryClient = useQueryClient();
  const selfPersonId = useSelfPersonId();

  const lede =
    portalText("core", "account_admin.organization_roles_decide_who_runs_the_organization_and_its_money_da2d3d");
  if (accountId === null) {
    return (
      <OrganizationPage tab="people" title={portalText("core", "account_admin.people_and_roles_df661e")} lede={lede}>
        <Block>
          <BlockNote>{open.loading ? portalText("core", "account_admin.loading_your_organization_a01331") : portalText("core", "account_admin.choose_an_organization_first_066988")}</BlockNote>
        </Block>
      </OrganizationPage>
    );
  }
  return (
    <OrganizationPage tab="people" title={portalText("core", "account_admin.people_and_roles_df661e")} lede={lede} outcome={outcome}>
      <Block
        title={portalText("core", "account_admin.organization_members_f81475")}
        buttons={
          canInvite ? (
            <Button variant="primary" onClick={() => setInviting({ email: "" })}>{portalText("core", "account_admin.invite_b13660")}</Button>
          ) : undefined
        }
      >
        {!canRead ? (
          <BlockNote>{portalText("core", "account_admin.only_an_owner_or_an_admin_of_the_organization_sees_its_people_093599")}</BlockNote>
        ) : member.isPending ? (
          <BlockNote>{portalText("core", "account_admin.loading_the_organization_s_people_d44cb3")}</BlockNote>
        ) : member.isError ? (
          <BlockNote>{apiProblemText(member.error, portalText("core", "account_admin.the_organization_s_people_could_not_be_loaded_c0d8fd"))}</BlockNote>
        ) : (
          <MemberTable
            member={member.data}
            caller={open.accountRole}
            selfPersonId={selfPersonId}
            onChange={setChanging}
            onGrant={(one) => setInviting({ email: one.email ?? "" })}
          />
        )}
      </Block>
      {canRead ? (
        <Block title={portalText("core", "account_admin.invitations_9b69a3")} dek={portalText("core", "account_admin.an_invitation_grants_exactly_the_roles_it_names_workspaces_added_aa3c4e")}>
          {invitation.isPending ? (
            <BlockNote>{portalText("core", "account_admin.loading_invitations_d13dc6")}</BlockNote>
          ) : invitation.isError ? (
            <BlockNote>{apiProblemText(invitation.error, portalText("core", "account_admin.the_invitations_could_not_be_loaded_b70412"))}</BlockNote>
          ) : (
            <InvitationColumn
              accountId={accountId}
              invitation={invitation.data}
              canInvite={canInvite}
              now={new Date()}
              onOutcome={setOutcome}
              onShare={setSharing}
            />
          )}
        </Block>
      ) : null}
      <Block title={portalText("core", "account_admin.what_organization_roles_can_do_ad0435")}>
        <AccountMatrix />
      </Block>
      <Block title={portalText("core", "account_admin.what_workspace_roles_can_do_0dd17a")} dek={portalText("core", "account_admin.developer_campaign_manager_and_billing_are_narrow_roles_for_peopl_832fb6")}>
        <WorkspaceMatrix />
      </Block>
      {canTransfer && member.data !== undefined ? (
        <Block title={portalText("core", "account_admin.transfer_ownership_a6819e")} dek={portalText("core", "account_admin.the_new_owner_must_be_an_active_member_with_a_verified_email_you_acd137")}>
          <TransferForm accountId={accountId} member={member.data} selfPersonId={selfPersonId} onOutcome={setOutcome} />
        </Block>
      ) : null}
      <ChangeRoleDialog
        accountId={accountId}
        member={changing}
        caller={open.accountRole}
        self={changing !== null && changing.personId === selfPersonId}
        onDone={(done) => {
          setChanging(null);
          if (done !== null) setOutcome(done);
        }}
      />
      {inviting === null ? null : (
        <InviteDialog
          key={inviting.email}
          accountId={accountId}
          open
          initialEmail={inviting.email}
          caller={open.accountRole}
          workspace={organizationWorkspaceReadOf(profile)}
          onClose={() => setInviting(null)}
          onSent={(sent) => {
            setInviting(null);
            void queryClient.invalidateQueries({ queryKey: invitationKey(accountId) });
            if (sent.delivery === "share_link") setSharing(sent);
            setOutcome({
              tone: "done",
              get text() { return sent.delivery === "email"
                  ? portalText("core", "account_admin.invitation_sent_it_and_can_be_resent_or_revoked_from_pending_belo_6a4a0d", { value0: expiryText(sent.invitation.expiresAt, new Date()) })
                  : portalText("core", "account_admin.invitation_created_it_send_its_link_yourself_6cae8a", { value0: expiryText(sent.invitation.expiresAt, new Date()) }); },
            });
          }}
        />
      )}
      <ShareLinkDialog sent={sharing} onClose={() => setSharing(null)} />
    </OrganizationPage>
  );
}

/** The signed-in person's id (to tell "you" apart in the table), from the shell's auth_session query. */
function useSelfPersonId(): string | null {
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway));
  return context.data?.person.personId ?? null;
}
